import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router';
import spottImg from '../../assets/Layer_1.svg';
import Button from '../../components/button/Button';
import Input from '../../components/input/Input';
import { useBrandContext } from '../../context/BrandContext';
import { useToast } from '../../context/ToastModalContext';
import { createUser } from '../../services/userService';
import {
  validateCompletName,
  validatePassword,
  validateRegexEmail,
} from '../../utils/format';
import EmailConfirmation from './emailConfirmation/EmailConfirmation';
import GoogleLoginButton from '../../components/GoogleLoginButton/GoogleLoginButton';
import AppleLoginButton from '../../components/AppleLoginButton/AppleLoginButton';
import { isWebView, isAppleDevice } from '../../utils/detectWebContainer';
import './Register.css';
import { useHeaderContext } from '../../context/HeaderContext';
import { getBuildNumber } from '../../utils/getBuildNumber';
import { useAuth } from '../../context/AuthContext';

const Register = () => {
  type Inputs = {
    name: string;
    email: string;
    password: string;
    passwordConfirmation: string;
  };
  const [form, setForm] = useState<Inputs>({
    name: '',
    email: '',
    password: '',
    passwordConfirmation: '',
  });
  type Touched = Partial<Record<keyof Inputs, boolean>>;
  const [touched, setTouched] = useState<Touched>({});
  type Errors = Partial<Record<keyof Inputs, string>>;
  const [errorsValidate, setErrorsValidate] = useState<Errors>({});
  const [errorFetch, setErrorFetch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [responseEmail, setResponseEmail] = useState('');
  const { showToast } = useToast();
  const location = useLocation();
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const navigate = useNavigate();
  const brandContext = useBrandContext();
  const authAppleShow = brandContext?.brandData?.features?.authApple;
  const authGoogleShow = brandContext?.brandData?.features?.authGoogle;
  const appleDevice = isAppleDevice() && authAppleShow;
  const webView = isWebView();

  const authContext = useAuth();
  const { setHeaderConfig } = useHeaderContext();
  const buildNumber = getBuildNumber(
    webView ? (authContext.appVersion as string) : null
  );
  const socialLoginVersion = 163;

  const comparerFieds = useCallback(() => {
    if (
      form.password === form.passwordConfirmation &&
      form.password !== '' &&
      !isLoading &&
      !modalOpen
    ) {
      setIsSubmitDisabled(false);
    } else {
      setIsSubmitDisabled(true);
    }
  }, [form.password, form.passwordConfirmation, isLoading, modalOpen]);

  useEffect(() => {
    comparerFieds();
  }, [comparerFieds]);

  // const routePaths = location.pathname.split('/');
  const validate = (newInputs: Inputs): Errors => {
    let newErrors: Errors = {};
    if (!newInputs.name.trim()) {
      newErrors.name = 'Campo Obrigatório';
    } else if (!validateCompletName(newInputs.name)) {
      newErrors.name = 'Por favor insira nome e sobrenome';
    }

    if (!newInputs.email.trim()) {
      newErrors.email = 'Campo Obrigatório';
    } else if (!validateRegexEmail(newInputs.email)) {
      newErrors.email = 'Por favor insira um e-mail válido';
    }

    if (!form.password.trim()) {
      newErrors.password = 'Campo Obrigatório';
    } else if (!validatePassword(newInputs.password)) {
      newErrors.password =
        'A senha precisa ter pelo menos 6 caracteres. \nPor favor, tente novamente.';
    }
    setErrorsValidate(newErrors);
    return newErrors;
  };

  const handleKeyPress = (event: any) => {
    if (event.key === ' ') {
      event.preventDefault();
    }
  };

  const handleShowPassword = () => {
    setShowPassword((showPassword) => !showPassword);
  };

  const handleShowPasswordConfirmation = () => {
    setShowPasswordConfirmation(
      (showPasswordConfirmation) => !showPasswordConfirmation
    );
  };

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const errors = validate(form);
    if (Object.keys(errors).length === 0) {
      setIsLoading(true);
      try {
        const response = await createUser(form.name, form.email, form.password);
        if (response?.email) {
          setResponseEmail(response?.email);
          sessionStorage.setItem('userEmail', JSON.stringify(response?.email));
        }
        setModalOpen(true);
        setIsLoading(false);
        setErrorFetch('');
      } catch (error: any) {
        setIsLoading(false);
        if (error?.response?.status === 409) {
          setErrorFetch('Este e-mail já possui uma conta.');
        } else if (error?.message === 'Network Error') {
          setErrorFetch(
            'Sua conexão está instável. Por favor, tente mais tarde.'
          );
        } else {
          setErrorFetch('Falha no servidor. Por favor tente novamente.');
        }
      }
    }
  };

  const handleTermsOfUse = () => {
    if (brandContext.brandData?.appTermsAndConditionsLink) {
      window.open(
        `/redir?directRedir=${brandContext.brandData.appTermsAndConditionsLink}`,
        '_blank'
      );
    } else {
      navigate(`/${brandContext.brandName}/terms-of-use`);
    }
  };

  const handlePolicy = () => {
    if (brandContext.brandData?.appPrivacyPolicyLink) {
      window.open(
        `/redir?directRedir=${brandContext.brandData.appPrivacyPolicyLink}`,
        '_blank'
      );
    } else {
      navigate(`/${brandContext.brandName}/privacy-policy`);
    }
  };

  useEffect(() => {
    setHeaderConfig({
      headerText: 'Crie sua conta',
      showLogo: true,
      showActionButtons: false,
    });
    return () => setHeaderConfig({});
  }, [navigate, setHeaderConfig]);

  return (
    <>
      <div className='register-container'>
        <form onSubmit={handleSubmit} className='register-form'>
          <div className='register-fields'>
            <Input
              labelName='Nome'
              htmlFor='name'
              type='text'
              id='name'
              disabled={modalOpen}
              className={
                errorsValidate.name
                  ? 'input-pattern error-input'
                  : 'input-pattern'
              }
              placeholder='Insira seu nome'
              value={form.name}
              onChange={(event) => {
                setForm({ ...form, name: event.target.value });
              }}
              onBlur={() => setTouched({ ...touched, name: true })}
              isErrorValidate={errorsValidate.name ? true : false}
              messageError={errorsValidate.name}
            />

            <Input
              labelName='E-mail'
              htmlFor='email'
              type='text'
              id='email'
              disabled={modalOpen}
              className={
                errorsValidate.email
                  ? 'input-pattern error-input'
                  : 'input-pattern'
              }
              placeholder='Insira seu e-mail'
              value={form.email}
              onKeyDown={(event) => handleKeyPress(event)}
              onChange={(event) => {
                setForm({ ...form, email: event.target.value.toLowerCase() });
              }}
              onBlur={() => setTouched({ ...touched, email: true })}
              isErrorValidate={errorsValidate.email ? true : false}
              messageError={errorsValidate.email}
              style={{
                textTransform: form.email.length > 0 ? 'lowercase' : 'unset',
              }}
            />

            <Input
              labelName='Senha'
              htmlFor='password'
              type={showPassword ? 'text' : 'password'}
              id='password'
              disabled={modalOpen}
              className={
                errorsValidate.password
                  ? 'input-pattern error-input'
                  : 'input-pattern'
              }
              placeholder='Insira sua senha'
              value={form.password}
              onKeyDown={(event) => handleKeyPress(event)}
              onChange={(event) => {
                setForm({ ...form, password: event.target.value });
              }}
              onBlur={() => setTouched({ ...touched, password: true })}
              isErrorValidate={errorsValidate.password ? true : false}
              isPassword={true}
              handleShowPassword={handleShowPassword}
              showPassword={showPassword}
              messageError={errorsValidate.password}
              style={{ flexGrow: 1, marginRight: '-19px' }}
            />
            <Input
              labelName='Confirme a sua senha'
              htmlFor='passwordConfirmation'
              type={showPassword ? 'text' : 'password'}
              id='passwordConfirmation'
              disabled={modalOpen}
              className={
                errorsValidate.passwordConfirmation
                  ? 'input-pattern error-input'
                  : 'input-pattern'
              }
              placeholder='Digite novamente a sua senha'
              value={form.passwordConfirmation}
              onKeyDown={(event) => handleKeyPress(event)}
              onChange={(event) => {
                setForm({ ...form, passwordConfirmation: event.target.value });
              }}
              onBlur={() =>
                setTouched({ ...touched, passwordConfirmation: true })
              }
              isErrorValidate={
                errorsValidate.passwordConfirmation ? true : false
              }
              isPassword={true}
              handleShowPasswordConfirmation={handleShowPasswordConfirmation}
              showPassword={showPassword}
              messageError={errorsValidate.passwordConfirmation}
              style={{ flexGrow: 1, marginRight: '-19px' }}
            />
            <div className='terms-of-use'>
              Ao se cadastrar, você declara estar ciente sobre os
              <br />
              <span onClick={() => handleTermsOfUse()}> Termos de uso </span>e
              <span onClick={() => handlePolicy()}>
                {' '}
                Política de privacidade.
              </span>
            </div>
            <b className='error-fetch-register'>
              {' '}
              {errorFetch ? errorFetch : null}
            </b>
            <Button
              type='submit'
              children='Crie Sua Conta'
              loading={isLoading}
              disabled={isLoading || modalOpen || isSubmitDisabled}
            />

            {!webView || buildNumber >= socialLoginVersion ? (
              <>
                <div className='divider-register'>OU</div>

                {authGoogleShow && <GoogleLoginButton />}

                {appleDevice && <AppleLoginButton />}
              </>
            ) : (
              ''
            )}
          </div>
          <div className='signup-link-container' style={{ marginTop: 20 }}>
            Já tem uma conta?&nbsp;
            <a href={`/${brandContext.brandName}/login`}>Faça o login</a>
          </div>
          {brandContext.brandName.toLowerCase() === 'spott' && (
            <div className='footer-spott-img'>
              <img alt='footer-spott-img' src={spottImg} />
            </div>
          )}
        </form>
      </div>
      {modalOpen && (
        <EmailConfirmation
          email={responseEmail}
          allowButtonClosed={true}
          isModalOpen={modalOpen}
          setIsModalOpen={setModalOpen}
        />
      )}
    </>
  );
};

export default Register;
